import React, { useEffect, useMemo, useRef, useState } from 'react';
import AskIcon from 'shared/components/ui/AskIcon';
import { createPortal } from 'react-dom';
import {
  Search, X, Plus, LayoutGrid, AlertTriangle, Timer, Coins, Bell,
} from 'lucide-react';
import BrandLoader from 'shared/components/ui/BrandLoader';
import { listAlertTemplates } from 'features/alerts/api/alertsApi';

/**
 * "New alert": start from a template, from scratch, or by describing it to
 * Lucid — the same pop-up as New eval.
 *
 * The templates are the server's catalog (GET /alerts/templates). Search reads
 * a template's name, description, when-to-use line, category and scope; the
 * rail narrows it. Picking one opens the editor filled in from it; nothing is
 * saved until the person saves.
 */

const SCOPE = { run: 'Run', span: 'Span' };

/** A face for each category; a bell for one this does not know yet. */
const CAT_ICON = { all: LayoutGrid, failures: AlertTriangle, latency: Timer, cost: Coins };
const iconFor = (id) => CAT_ICON[id] || Bell;

/** The three to reach for first. */
const START_HERE = ['hard_failures', 'error_rate', 'slow_runs'];

/* As on New eval: the panel rises in, the starting points just behind it;
   both off under prefers-reduced-motion. */
const MOTION = `
.alert-template-dialog { animation: alertDialogIn 160ms cubic-bezier(0.16, 1, 0.3, 1) both; }
.alert-template-card { animation: alertCardIn 220ms cubic-bezier(0.16, 1, 0.3, 1) both; }
@keyframes alertDialogIn { from { opacity: 0; transform: translateY(-8px) scale(0.985); } to { opacity: 1; transform: none; } }
@keyframes alertCardIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) {
  .alert-template-dialog, .alert-template-card { animation: none; }
}`;

export function matchesTemplate(t, q) {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  const hay = [t.name, t.description, t.when, t.category, SCOPE[t.scope] || t.scope].join(' ').toLowerCase();
  return s.split(/\s+/).every((w) => hay.includes(w));
}

/** What a template watches, as the list page tags an alert. */
function ScopePill({ scope }) {
  return (
    <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]" title={`Watches ${scope}s`}>
      {SCOPE[scope] || scope}
    </span>
  );
}

export default function NewAlertDialog({ open, onClose, onPick, onScratch, onAsk }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open || data) return undefined;
    let off = false;
    Promise.resolve(listAlertTemplates())
      .then((d) => { if (!off) setData(d); })
      .catch((e) => { if (!off) setError(e?.response?.data?.error || e.message); });
    return () => { off = true; };
  }, [open, data]);

  useEffect(() => {
    if (!open) return undefined;
    setQ(''); setCat('all');
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);

  const categories = useMemo(() => data?.categories || [], [data]);
  const all = useMemo(() => data?.templates || [], [data]);
  const shown = useMemo(() => all.filter((t) => (cat === 'all' || t.category === cat) && matchesTemplate(t, q)), [all, cat, q]);
  const grouped = useMemo(() => categories
    .map((c) => ({ ...c, items: shown.filter((t) => t.category === c.id) }))
    .filter((g) => g.items.length), [categories, shown]);
  const counts = useMemo(() => {
    const m = { all: all.length };
    for (const t of all) m[t.category] = (m[t.category] || 0) + 1;
    return m;
  }, [all]);
  // Only when nothing is searched or narrowed: then what they look for is the point.
  const starters = useMemo(() => (q.trim() || cat !== 'all'
    ? []
    : START_HERE.map((id) => all.find((t) => t.id === id)).filter(Boolean)), [all, q, cat]);

  if (!open) return null;

  const el = (
    <div className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[7vh]"
      style={{ background: 'color-mix(in srgb, var(--text-primary) 40%, transparent)' }}
      role="dialog" aria-modal="true" aria-labelledby="new-alert-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{MOTION}</style>
      <div className="alert-template-dialog flex max-h-[84vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-[color:var(--border-default)] bg-[color:var(--surface-base)] shadow-2xl"
        style={{ fontFamily: 'Inter, sans-serif' }}>
        {/* On a phone the two ways in drop to their own row, so nothing wraps mid-label. */}
        <div className="flex flex-wrap items-center gap-2 border-b border-[color:var(--border-subtle)] px-4 py-3">
          <h2 id="new-alert-title" className="whitespace-nowrap text-[14px] font-semibold text-[color:var(--text-primary)]">New alert</h2>
          <div className="flex-1" />
          <div className="order-last flex w-full gap-2 sm:order-none sm:w-auto">
            <button type="button" onClick={onAsk} title="Describe the alert to Lucid and it builds it"
              className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-[color:var(--border-default)] px-3 text-[12px] font-medium text-[color:var(--text-secondary)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] sm:flex-none">
              <AskIcon size={14} /> Create with Lucid
            </button>
            <button type="button" onClick={onScratch}
              className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-brand px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover sm:flex-none">
              <Plus size={13} aria-hidden /> Start from scratch
            </button>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"><X size={16} /></button>
        </div>

        <div className="border-b border-[color:var(--border-subtle)] px-4 py-2.5">
          <div className="flex h-9 items-center gap-2 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-3 focus-within:border-[color:var(--border-strong)]">
            <Search size={14} className="shrink-0 text-[color:var(--text-muted)]" aria-hidden />
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates: failures, latency, cost…"
              aria-label="Search templates" className="min-w-0 flex-1 bg-transparent text-[13px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)]" />
            {q && <button type="button" onClick={() => setQ('')} aria-label="Clear search" className="text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"><X size={13} /></button>}
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          <nav className="hidden w-[186px] shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-[color:var(--border-subtle)] px-2 py-2.5 sm:flex" aria-label="Categories">
            <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-muted)]">Browse</div>
            {[{ id: 'all', label: 'All' }, ...categories].map((c) => {
              const Icon = iconFor(c.id);
              const on = cat === c.id;
              return (
                <button key={c.id} type="button" role="radio" aria-checked={on} onClick={() => setCat(c.id)}
                  className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12px] transition-colors ${on
                    ? 'bg-surface-hover font-medium text-[color:var(--text-primary)]'
                    : 'text-[color:var(--text-secondary)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]'}`}>
                  <Icon size={14} className={on ? 'text-brand' : 'text-[color:var(--text-muted)]'} aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{c.label}</span>
                  <span className="shrink-0 text-[10.5px] tabular-nums text-[color:var(--text-muted)]">{counts[c.id] || 0}</span>
                </button>
              );
            })}
          </nav>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3" data-testid="template-list">
            {error && <div className="text-[12px] text-danger" role="alert">Templates could not load: {error}</div>}
            {!data && !error && <div className="flex justify-center py-10"><BrandLoader label="Loading templates" /></div>}
            {data && shown.length === 0 && (
              <div className="py-10 text-center text-[12px] text-[color:var(--text-muted)]">
                No template matches “{q}”. <button type="button" onClick={onScratch} className="font-medium text-[color:var(--text-primary)] underline-offset-2 hover:underline">Start from scratch</button> or <button type="button" onClick={onAsk} className="font-medium text-[color:var(--text-primary)] underline-offset-2 hover:underline">describe it to Lucid</button>.
              </div>
            )}

            {starters.length > 0 && (
              <section className="mb-5" aria-label="Start here">
                <h3 className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">Start here</h3>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {starters.map((t, i) => {
                    const Icon = iconFor(t.category);
                    return (
                      <button key={t.id} type="button" onClick={() => onPick(t)} data-testid={`starter-${t.id}`}
                        style={{ animationDelay: `${i * 45}ms` }}
                        className="alert-template-card group flex flex-col items-start gap-1.5 rounded-xl border border-[color:var(--border-default)] bg-surface p-3 text-left transition-all hover:-translate-y-0.5 hover:border-[color:var(--border-strong)] hover:shadow-token-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]">
                        <span className="flex w-full items-center gap-2">
                          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-soft text-brand transition-transform group-hover:scale-105">
                            <Icon size={14} aria-hidden />
                          </span>
                          <ScopePill scope={t.scope} />
                        </span>
                        <span className="text-[13px] font-medium leading-snug text-[color:var(--text-primary)]">{t.name}</span>
                        <span className="line-clamp-2 text-[11.5px] leading-snug text-[color:var(--text-secondary)]">{t.description}</span>
                        {t.when && <span className="text-[10.5px] text-[color:var(--text-muted)]">{t.when}</span>}
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {grouped.map((g) => (
              <section key={g.id} className="mb-4" aria-label={g.label}>
                <h3 className="mb-1 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
                  {React.createElement(iconFor(g.id), { size: 12, 'aria-hidden': true })}
                  {g.label}
                </h3>
                <div className="flex flex-col">
                  {g.items.map((t) => (
                    <button key={t.id} type="button" onClick={() => onPick(t)} data-testid={`template-${t.id}`}
                      className="group -mx-1.5 flex items-center gap-3 rounded-lg px-1.5 py-2 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]">
                      <span className="flex w-[160px] shrink-0 items-center gap-2 sm:w-[210px]">
                        <span className="truncate text-[13px] font-medium text-[color:var(--text-primary)]">{t.name}</span>
                        <ScopePill scope={t.scope} />
                      </span>
                      <span className="line-clamp-1 min-w-0 flex-1 text-[12px] text-[color:var(--text-secondary)]">{t.description}</span>
                      {t.when && <span className="hidden shrink-0 text-[11px] text-[color:var(--text-muted)] md:inline">{t.when}</span>}
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
  return createPortal(el, document.body);
}
